// client/src/AlphaMessage.js
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CantoAtmosphere from './components/CantoAtmosphere';
import HostedAppDemo from './components/HostedAppDemo';
import ModernHero from './components/ModernHero'; // Import modern hero
import PoetsBench from './components/PoetsBench';
import ProductJourneyStill from './components/ProductJourneyStill';
import SEO from './components/SEO'; // Import SEO component
import { CantoMark, PaintedHeader, PAINTINGS, Placard } from './components/paintings';
import useLantern from './hooks/useLantern';
import MobileLandingPage from './mobile/MobileLandingPage';
import philosopherRembrandt from './assets/philosopher-rembrandt.jpg';
import './styles.css';

// --- Muses Carousel (model cards with swipe on mobile) ---
const OpenAiMark = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" fillRule="evenodd" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M9.205 8.658v-2.26c0-.19.072-.333.238-.428l4.543-2.616c.619-.357 1.356-.523 2.117-.523 2.854 0 4.662 2.212 4.662 4.566 0 .167 0 .357-.024.547l-4.71-2.759a.797.797 0 00-.856 0l-5.97 3.473zm10.609 8.8V12.06c0-.333-.143-.57-.429-.737l-5.97-3.473 1.95-1.118a.433.433 0 01.476 0l4.543 2.617c1.309.76 2.189 2.378 2.189 3.948 0 1.808-1.07 3.473-2.76 4.163zM7.802 12.703l-1.95-1.142c-.167-.095-.239-.238-.239-.428V5.899c0-2.545 1.95-4.472 4.591-4.472 1 0 1.927.333 2.712.928L8.23 5.067c-.285.166-.428.404-.428.737v6.898zM12 15.128l-2.795-1.57v-3.33L12 8.658l2.795 1.57v3.33L12 15.128zm1.796 7.23c-1 0-1.927-.332-2.712-.927l4.686-2.712c.285-.166.428-.404.428-.737v-6.898l1.974 1.142c.167.095.238.238.238.428v5.233c0 2.545-1.974 4.472-4.614 4.472zm-5.637-5.303l-4.544-2.617c-1.308-.761-2.188-2.378-2.188-3.948A4.482 4.482 0 014.21 6.327v5.423c0 .333.143.571.428.738l5.947 3.449-1.95 1.118a.432.432 0 01-.476 0zm-.262 3.9c-2.688 0-4.662-2.021-4.662-4.519 0-.19.024-.38.047-.57l4.686 2.71c.286.167.571.167.856 0l5.97-3.448v2.26c0 .19-.07.333-.237.428l-4.543 2.616c-.619.357-1.356.523-2.117.523zm5.899 2.83a5.947 5.947 0 005.827-4.756C22.287 18.339 24 15.84 24 13.296c0-1.665-.713-3.282-1.998-4.448.119-.5.19-.999.19-1.498 0-3.401-2.759-5.947-5.946-5.947-.642 0-1.26.095-1.88.31A5.962 5.962 0 0010.205 0a5.947 5.947 0 00-5.827 4.757C1.713 5.447 0 7.945 0 10.49c0 1.666.713 3.283 1.998 4.448-.119.5-.19 1-.19 1.499 0 3.401 2.759 5.946 5.946 5.946.642 0 1.26-.095 1.88-.309a5.96 5.96 0 004.162 1.713z" />
  </svg>
);

const OpenSourceMark = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M9 6l-6 6 6 6M15 6l6 6-6 6" stroke="#F0BB8A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MUSES = [
  {
    icon: <img src="/icons/model_icons/claude-color.svg" alt="Claude" />,
    title: "Anthropic",
    subtitle: "Claude Opus 5.5",
    tagline: "A brilliant writer. A master orchestrator.",
    desc: "Anthropic’s new Opus 5.5 opens up the creative process from every angle. It pairs a storyteller’s ear for dialogue, subtext, and character with the orchestration skills to carry an ambitious project beyond the page. Develop the idea, shape the script, plan the shots, and direct the next move across Virgil’s creative tools."
  },
  {
    icon: <OpenAiMark />,
    title: "OpenAI",
    subtitle: "GPT-6.1 Sol Ultra",
    tagline: "Big ideas. Deep reasoning. A lighter price.",
    desc: "Sol turns scattered notes into architecture: bold outlines, richly imagined worlds, and rewrites that hold the whole story together. GPT-6.1 brings near-Astra capability at a fraction of Astra’s token cost. Ultra turns reasoning all the way up. When the next move isn’t obvious, this is the mind you want on it."
  },
  {
    icon: <img src="/icons/model_icons/gemini-color.svg" alt="Gemini" />,
    title: "Google",
    subtitle: "Gemini 3.1 Pro",
    tagline: "Best for huge context, visual reference boards, and efficient coverage.",
    desc: "Gemini 3.1 Pro gives Virgil a broad, practical pass when you want scripts, bibles, notes, images, and research considered together without spending flagship-model energy on every iteration."
  },
  {
    icon: <img src="/icons/model_icons/grok.svg" alt="Grok" className="icon-grok" />,
    title: "xAI",
    subtitle: "Grok 4.7",
    tagline: "Explore sharper dialogue, alternatives, and critique.",
    desc: "Grok 4.7 is Virgil’s xAI default for exploring punchier dialogue, contrarian options, and direct notes. Use it to challenge a scene, test a new angle, or compare a different take on your draft."
  },
  {
    icon: <OpenSourceMark />,
    title: "Open Source",
    subtitle: "DeepSeek V4 Pro, Kimi K3 & more",
    tagline: "Best for high-volume exploration without breaking the bank.",
    desc: "Open models give Virgil room to generate alternates: ten taglines, twenty scene starts, quick summaries, low-stakes rewrites, and draft experiments you can sift through fast."
  }
];

function ModelGuides() {
  const [activeIdx, setActiveIdx] = useState(0);
  const muse = MUSES[activeIdx];

  return (
    <div className="model-guides">
      <div className="model-guides-rail" role="tablist" aria-label="Model guides">
        {MUSES.map((m, i) => (
          <button
            key={m.title}
            type="button"
            role="tab"
            aria-selected={i === activeIdx}
            className={`model-guides-tab ${i === activeIdx ? 'is-active' : ''}`}
            onClick={() => setActiveIdx(i)}
          >
            <span className="model-guides-tab-index">{String(i + 1).padStart(2, '0')}</span>
            <span className="model-guides-tab-text">
              <span className="model-guides-tab-name">{m.title}</span>
              <span className="model-guides-tab-sub">{m.subtitle}</span>
            </span>
          </button>
        ))}
      </div>
      <div className="model-guides-pane" key={muse.title} role="tabpanel">
        <div className="model-guides-icon">{muse.icon}</div>
        <p className="model-guides-tagline">{muse.tagline}</p>
        <p className="model-guides-desc">{muse.desc}</p>
      </div>
    </div>
  );
}

// A full-bleed painting with one line of copy over it and a museum placard
// in the corner. `canto` makes it a stop on the gold thread.
function ArtChapter({
  variant,
  id,
  canto,
  cantoTitle,
  eyebrow,
  quote,
  painting,
  align = 'center',
  lantern = false,
  className = '',
  children,
}) {
  const sectionRef = useRef(null);
  useLantern(sectionRef, lantern);

  return (
    <section
      ref={sectionRef}
      id={id}
      className={`art-chapter art-chapter--${variant} art-chapter--align-${align} ${lantern ? 'has-lantern' : ''} ${className}`.trim()}
      aria-label={eyebrow}
      data-canto={canto}
      data-canto-title={cantoTitle}
    >
      <div className="art-chapter-art" aria-hidden="true" />
      <div className="art-chapter-veil" aria-hidden="true" />
      {lantern && <div className="art-lantern" aria-hidden="true" />}
      <div className="art-chapter-inner">
        {canto && <CantoMark numeral={canto} />}
        <span className="eyebrow art-chapter-eyebrow">{eyebrow}</span>
        <p className="art-chapter-quote">{quote}</p>
        {children}
      </div>
      {painting && <Placard painting={painting} className="art-chapter-placard" />}
    </section>
  );
}

const MISSION_PILLARS = [
  {
    kicker: 'Clarity',
    title: 'Focus the creative signal',
    copy: 'Fewer distractions, sharper hierarchy, and surfaces that help you decide what matters next.',
  },
  {
    kicker: 'Momentum',
    title: 'Keep the draft moving',
    copy: 'Formatting, rewrites, ideation, and scene building should feel like one uninterrupted motion.',
  },
  {
    kicker: 'Authorship',
    title: 'Protect the writer on the page',
    copy: 'The tool can suggest, generate, and challenge, but the voice remains unmistakably yours.',
  },
];

// One Inferno project, carried through the complete creative pipeline.
const FEATURE_GALLERY = [
  {
    type: 'projects',
    eyebrow: '01 / Projects',
    title: 'Your work, wherever you are',
    description: 'Cloud-synced projects keep every draft and visual ready on desktop or on the go.',
  },
  {
    type: 'idea',
    eyebrow: '02 / Writing',
    title: 'Start with an idea',
    description: 'Write in your own voice while Virgil helps you explore the next move.',
  },
  {
    type: 'storyboard',
    eyebrow: '03 / Storyboard',
    title: 'Turn it into a storyboard',
    description: 'Translate pages into visual beats without losing the creative thread.',
  },
  {
    type: 'video',
    eyebrow: '04 / Video',
    title: 'Bring your vision to life',
    description: 'Carry those shots into a working timeline and shape the final cut.',
    badge: 'Beta',
  },
];

// Main Landing Page Component (formerly AlphaMessage)
function LandingPage({ useMobileVersion = false }) { // Rename component for clarity
  if (useMobileVersion) {
    return <MobileLandingPage />;
  }

  return <DesktopLandingPage />;
}

function DesktopLandingPage() {
  // Lightbox state
  const [selectedImage, setSelectedImage] = useState(null);

  const openLightbox = (imageInfo) => {
    setSelectedImage(imageInfo);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  useEffect(() => {
    if (!selectedImage) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedImage(null);
        document.body.style.overflow = 'auto';
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [selectedImage]);



  // --- Scroll Animation State & Logic ---
  const [visibleSections, setVisibleSections] = useState({});
  const sectionRefs = {
    intro: useRef(null),
    screenshots: useRef(null),
    demo: useRef(null),
    features: useRef(null)
  };

  useEffect(() => {
    const observerOptions = {
      root: null, // relative to document viewport
      rootMargin: '0px',
      threshold: 0.1 // trigger when 10% of the element is visible
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisibleSections(prev => ({ ...prev, [entry.target.id]: true }));
          // Optional: Unobserve after becoming visible to save resources
          // observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all sections
    Object.values(sectionRefs).forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    // Cleanup function
    return () => {
       Object.values(sectionRefs).forEach(ref => {
         if (ref.current) {
           // eslint-disable-next-line react-hooks/exhaustive-deps
           observer.unobserve(ref.current);
         }
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run observer setup once on mount



  return (
    <div className="page-container"> {/* Use main container class */}
      <SEO 
        title="Virgil | AI Writing Workspace for Screenplays and Novels"
        description="Outline, draft, revise, format, and explore visual ideas with multiple AI models in one workspace built for screenplays, novels, podcasts, and pitches."
        keywords="AI creative writing, AI writing workspace, screenwriting AI, novel writing AI, screenplay formatting, multi-model AI writer"
      /> {/* Add SEO meta tags */}
      <CantoAtmosphere />
      {/* Render the hero + live demo as one seamless stage */}
      <ModernHero
        sectionId="demo-chatbot"
        sectionRef={sectionRefs.demo}
        className="hero-demo-stage"
      >
        <HostedAppDemo variant="hero" showCopy={false} />
      </ModernHero>

      {/* Beyond the demo: scannable feature gallery for visitors who skip the live demo */}
      <section
        id="screenshots"
        ref={sectionRefs.screenshots}
        className={`section-wrap feature-gallery-section reveal-on-scroll ${visibleSections['screenshots'] ? 'is-visible' : ''}`}
        data-canto="I"
        data-canto-title="Inside the studio"
      >
        <div className="section-intro section-intro--split">
          <CantoMark numeral="I" />
          <span className="eyebrow">Inside the studio</span>
          <h2 className="section-title-xl">From first idea to final cut</h2>
          <p className="section-lede">
            One continuous creative pipeline. Your project, your voice, and every step of the work stay together.
          </p>
        </div>
        <div id="product-gallery" className="journey-gallery-grid">
          {FEATURE_GALLERY.map((feature, index) => (
            <button
              type="button"
              key={feature.title}
              id={`product-gallery-card-${index + 1}`}
              className="journey-gallery-card feature-gallery-card"
              onClick={() => openLightbox({
                type: feature.type,
                title: feature.title,
                description: feature.description,
              })}
              aria-label={`Open ${feature.title} preview`}
            >
              <div className="journey-gallery-media">
                {feature.badge && <span className="feature-gallery-badge">{feature.badge}</span>}
                <ProductJourneyStill type={feature.type} loading="lazy" />
              </div>
              <div className="journey-gallery-copy">
                <span className="journey-gallery-eyebrow">{feature.eyebrow}</span>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Full-bleed art chapters give each major section a distinct visual world. */}
      <ArtChapter
        variant="delacroix"
        id="why-virgil"
        canto="II"
        cantoTitle="Why Virgil"
        eyebrow="Why “Virgil”"
        quote={(
          <>
            Every draft has its dark wood: the place where the story stalls and the way forward disappears.
            <br />
            Virgil is named for the guide who walks you out.
          </>
        )}
        painting={PAINTINGS.delacroix}
        lantern
      />

      {/* The page walks from night into day in acts. Each act paints its own
          ground, so keep a painting between two acts: its edges hide the seam. */}
      <div className="canto-act canto-act--dawn" data-act="dawn">
        <section 
          id="intro-copy" 
          ref={sectionRefs.intro} 
          className={`content-section intro-copy-section mission-section reveal-on-scroll ${visibleSections['intro-copy'] ? 'is-visible' : ''}`}
          data-canto="III"
          data-canto-title="Our mission"
        >
          <div className="mission-wrap">
            <div className="section-intro section-intro--split">
              <CantoMark numeral="III" />
              <span className="eyebrow">Our Mission</span>
              <h3 className="section-title-xl mission-title">Put serious storytelling tools in more hands</h3>
              <p className="section-lede mission-lede">
                We build tools that help writers finish work without flattening the voice that makes it theirs.
              </p>
            </div>

            <div className="mission-pillar-grid">
              {MISSION_PILLARS.map((pillar) => (
                <article key={pillar.title} className="mission-card">
                  <span className="mission-card-kicker">{pillar.kicker}</span>
                  <h4>{pillar.title}</h4>
                  <p>{pillar.copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      </div>

      <ArtChapter
        variant="parnassus"
        id="poets-bench"
        canto="IV"
        cantoTitle="The Poets' Bench"
        eyebrow="The Poets' Bench"
        quote={(
          <>
            Writers have argued about craft
            <br />
            for two thousand years. Pull up a chair.
          </>
        )}
        painting={PAINTINGS.parnassus}
      >
        <PoetsBench layout="row" />
      </ArtChapter>

      {/* Daylight from the fresco to the footer. Only the model guides take the
          day palette (`daylight`); the closing painting keeps its night type. */}
      <div className="canto-act canto-act--day" data-act="day">
        <section 
          id="features" 
          ref={sectionRefs.features}
          className={`content-section features-section daylight reveal-on-scroll ${visibleSections['features'] ? 'is-visible' : ''}`}
          data-canto="V"
          data-canto-title="Model guides"
        >
          {/* The Rembrandt opens the model guides instead of standing alone. */}
          <PaintedHeader
            art={philosopherRembrandt}
            painting={PAINTINGS.philosopher}
            eyebrow="A mind at work"
            quote="Different questions demand different kinds of intelligence."
          />
          <div className="section-intro section-intro--split">
            <CantoMark numeral="V" />
            <span className="eyebrow">Model guides</span>
            <h2 className="section-title-xl">Choose the intelligence that best serves the pass</h2>
            <p className="section-lede">
              Every page, every scene, every line poses its own problem. Choose the model that best clears
              the hurdle: narrative structure, dialogue, visuals, or quick exploration.
            </p>
          </div>
          <ModelGuides />
          <p className="muses-outro-paragraph">
            Outline with one model, sharpen with another, then bring the work back into your own voice.
          </p>
        </section>

        {/* The last painting is also the close: the invitation sits on it. */}
        <ArtChapter
          variant="virgil-tomb"
          id="closing"
          canto="VI"
          cantoTitle="The oldest collaboration"
          eyebrow="The oldest collaboration"
          quote={(
            <>
              Dante had one. Milton wanted one.
              <br />
              Now every writer <span className="nowrap">gets a Virgil.</span>
            </>
          )}
          painting={PAINTINGS.virgilTomb}
          align="left"
          className="art-chapter--finale"
        >
          <p className="art-chapter-cta-lede">Outline, draft, revise, storyboard, and polish in one place.</p>
          <div className="cta-buttons art-chapter-cta">
            <button
              type="button"
              onClick={() => { window.location.href = '/signup'; }}
              className="cta-button"
            >
              Start your 14-day trial
            </button>
            <button
              type="button"
              onClick={() => { window.location.href = '/download'; }}
              className="cta-button secondary"
            >
              Download
            </button>
          </div>
        </ArtChapter>
      </div>

      {/* Lightbox Overlay for Landing Page */}
      {selectedImage && createPortal((
        <div
          className="lightbox-overlay"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={selectedImage.title}
        >
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox} aria-label="Close image preview">✕</button>
            <ProductJourneyStill type={selectedImage.type} expanded />
            <div className="lightbox-caption">
              <h3>{selectedImage.title}</h3>
              <p>{selectedImage.description}</p>
            </div>
          </div>
        </div>
      ), document.body)}

    </div>
  );
}

export default LandingPage; // Export renamed component
